import {
  Button,
  Col,
  Form,
  Row,
  Input,
  message,
  DatePicker,
  Select,
} from "antd";
import React, { useEffect, useState } from "react";
import moment from "moment";
import { OrderStatus } from "_helpers";
import {
  companyService,
  materialTypeService,
  stockReceiptService,
  stockClassificationService,
} from "_services";

function Search(props) {
  const [isLoadingSuppliers, setIsLoadingSuppliers] = useState(false);
  const [isLoadingStockTypes, setIsLoadingStockTypes] = useState(false);
  const [isLoadingStockClasses, setIsLoadingStockClasses] = useState(false);
  const [stockTypes, setStockTypes] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [stockClasses, setStockClasses] = useState([]);

  const [form] = Form.useForm();
  const { Option } = Select;

  useEffect(() => {
    getStockTypes();
    getSupplierCompanies();
    getStockClassifications();
  }, []);

  const getStockTypes = () => {
    materialTypeService
      .getSuggestion()
      .then((x) => {
        setStockTypes(x.content);
      })
      .catch((error) => {
        message.error(`${error}`);
      });
  };

  const getSupplierCompanies = () => {
    setIsLoadingSuppliers(true);
    companyService
      .getSuggestion("?companyTypeCodes=SUP,MSUP")
      .then((x) => {
        setIsLoadingSuppliers(false);
        setSuppliers(x.content);
      })
      .catch((errorInfo) => {
        setIsLoadingSuppliers(false);
        message.error(`${errorInfo}`);
      });
  };

  const getStockClassifications = () => {
    setIsLoadingStockClasses(true);
    stockClassificationService
      .getSuggestion()
      .then((x) => {
        setIsLoadingStockClasses(false);
        setStockClasses(x.content);
      })
      .catch((errorInfo) => {
        setIsLoadingStockClasses(false);
        message.error(`${errorInfo}`);
      });
  };

  const onFinish = (values) => {
    props.showSearchLoading(true);
    let searchParams = "";
    if (values.receiptNumber) {
      searchParams += searchParams.indexOf("?") === -1 ? "?" : "&";
      searchParams += `receiptNumber=${encodeURIComponent(
        values.receiptNumber
      )}`;
    }
    if (values.orderStage) {
      searchParams += searchParams.indexOf("?") === -1 ? "?" : "&";
      searchParams += `orderStages=${encodeURIComponent(
        values.orderStage.toString()
      )}`;
    }
    if (values.orderDateRange) {
      searchParams += searchParams.indexOf("?") === -1 ? "?" : "&";
      searchParams += `fromDate=${encodeURIComponent(
        moment(values.orderDateRange[0]).startOf("day").format("YYYY-MM-DD")
      )}&toDate=${moment(values.orderDateRange[1])
        .startOf("day")
        .format("YYYY-MM-DD")}`;
    }
    if (values.stockTypeId) {
      searchParams += searchParams.indexOf("?") === -1 ? "?" : "&";
      searchParams += `stockTypeId=${encodeURIComponent(values.stockTypeId)}`;
    }
    if (values.refNumber) {
      searchParams += searchParams.indexOf("?") === -1 ? "?" : "&";
      searchParams += `refNumber=${encodeURIComponent(values.refNumber)}`;
    }
    if (values.stockClassification) {
      searchParams += searchParams.indexOf("?") === -1 ? "?" : "&";
      searchParams += `stockClassification=${encodeURIComponent(
        values.stockClassification
      )}`;
    }
    stockReceiptService
      .searchByView(searchParams)
      .then((data) => {
        props.onSearchResults(
          data.content,
          data.pagination.totalRecords,
          searchParams
        );
      })
      .catch((error) => {
        message.error(`${error}`);
        props.onSearchResults([]);
      });
  };

  return (
    <Form
      form={form}
      onFinish={onFinish}
      layout="vertical"
      className="search-form"
    >
      <Row gutter={24}>
        <Col span={6}>
          <Form.Item name="receiptNumber" label="Receipt Number">
            <Input.Search placeholder="Receipt Number" allowClear />
          </Form.Item>
        </Col>
        <Col span={6}>
          <Form.Item name="orderDateRange" label="Receipt Date">
            <DatePicker.RangePicker
              ranges={{
                Today: [moment(), moment()],
                "This Month": [
                  moment().startOf("month"),
                  moment().endOf("month"),
                ],
              }}
            />
          </Form.Item>
        </Col>
        <Col span={6}>
          <Form.Item name="orderStage" label="Status">
            <Select allowClear mode="multiple">
              {OrderStatus.map((t) => (
                <Select.Option value={t.value} key={t.value}>
                  {t.name}
                </Select.Option>
              ))}
            </Select>
          </Form.Item>
        </Col>
        <Col span={6} style={{ textAlign: "right" }}>
          <div>
            <Button type="primary" htmlType="submit">
              Search
            </Button>
            <Button
              style={{ margin: "0 8px" }}
              onClick={() => {
                form.resetFields();
                onFinish({});
              }}
            >
              Clear
            </Button>
          </div>
        </Col>
      </Row>
      <Row gutter={24}>
        <Col span={6}>
          <Form.Item name="stockTypeId" label="Material Type">
            <Select
              showSearch
              allowClear
              showArrow={false}
              optionFilterProp="children"
              placeholder="Material Type"
              loading={isLoadingStockTypes}
            >
              {stockTypes.map((t) => (
                <Select.Option value={t.id} key={t.id}>
                  {t.code}
                </Select.Option>
              ))}
            </Select>
          </Form.Item>
        </Col>
        <Col span={6}>
          <Form.Item name="refNumber" label="Ref. Number">
            <Input.Search placeholder="Ref. Number" allowClear />
          </Form.Item>
        </Col>
        <Col span={6}>
          <Form.Item name="supplierId" label="Supplier">
            <Select
              showSearch
              allowClear
              showArrow={false}
              optionFilterProp="children"
              placeholder="Supplier"
              loading={isLoadingSuppliers}
            >
              {suppliers.map((t) => (
                <Select.Option value={t.id} key={t.id}>
                  {t.name} ({t.code})
                </Select.Option>
              ))}
            </Select>
          </Form.Item>
        </Col>
        <Col span={6}>
          <Form.Item name="stockClassification" label="Stock Category">
            <Select
              placeholder="Please select"
              allowClear
              loading={isLoadingStockClasses}
            >
              {stockClasses.length > 0 &&
                stockClasses.map((t) => (
                  <Select.Option value={t.name} key={t.id}>
                    {t.name}
                  </Select.Option>
                ))}
            </Select>
          </Form.Item>
        </Col>
      </Row>
    </Form>
  );
}

export default Search;
